<script lang="ts">
  import { Dialog, DialogContent, DialogFooter, DialogTitle, DialogDescription } from '$lib/components/ui/dialog';
  import { Button } from '$lib/components/ui/button';
  
  import TitleEditor from "./TitleEditor.svelte";
  import DateEditor from "./DateEditor.svelte";
  import DescriptionEditor from "./DescriptionEditor.svelte";
  import StampInSetRow from '../itemlist/StampInSetRow.svelte';
  import StampInSetEditor from './StampInSetEditor.svelte';
  
  import { SetEditorModel } from './models/SetEditorModel.svelte';
  import { StampInSetModel } from './models/StampInSetModel.svelte';
  import LabelsEditor from "./LabelsEditor.svelte";
  import ZhihaoEditor from "./ZhihaoEditor.svelte";
  import { getLabelsForType } from "$lib/philatelic-labels";
  import { PhilatelicItemType } from "@briefmarken_katalog/types";
  import { ChevronUp, ChevronDown, Trash2, Check } from "@lucide/svelte";
  import { t } from "$lib/i18n";
  import { createBindableState, type BindableValue } from './utils.svelte';
  import HelpButton from "../HelpButton.svelte";

  interface Props {
    isOpen: BindableValue<boolean>;
    model:  SetEditorModel;
  }

  let { isOpen = $bindable(false), model = $bindable() }: Props = $props();

  let open = createBindableState(
    () => isOpen,
    (v) => isOpen = v,
    false
  );

  let isSetInStampEditorOpen = $state(false);
  /** Index whose delete button was armed and is waiting for the confirming click. */
  let pendingRemoval = $state<number | null>(null);
  let stampInSetModel = $state(new StampInSetModel({
    onCreate: model.onStampAdded.bind(model),
    isReadonly: model.isReadonly,
    defaultTitle: model.title,
  }));

</script>
 
<Dialog bind:open={open.current}>
  <DialogContent class="sm:max-w-2xl flex flex-col overflow-hidden max-h-[90dvh]">
    <div class="flex items-center gap-2">
      <DialogTitle>{$t(model.dialogTitle)}</DialogTitle>
      <HelpButton sectionId="set-editor" />
    </div>
    <DialogDescription>{$t(model.dialogSubtitle)}{model.dialogSubtitleSuffix}</DialogDescription>
    <div class="grid gap-4 py-4 overflow-y-auto flex-1 min-h-0">
      <TitleEditor isReadonly={model.isReadonly} bind:title={model.title} invalid={!model.isReadonly && model.isTitleInvalid}/>
      <DescriptionEditor
        isReadonly={model.isReadonly}
        bind:description = {model.description}
      />
      <DateEditor
        isReadonly={model.isReadonly}
        bind:date={model.date_of_issue}
        bind:expiryDate={model.expiry_date}
        invalid={!model.isReadonly && model.isDateInvalid}
      />
      <div>
        <span class={!model.isReadonly && model.isStampsInvalid ? 'text-destructive' : ''}>{$t('editor.stamps')} ({model.stamps.length})</span>
        <div class={model.stamps.length > 3 ? 'overflow-y-auto max-h-48' : ''}>
          {#each model.stamps as stamp, i}
          <div>
            <StampInSetRow {stamp} readonly={model.isReadonly} parentZhihao={model.effectiveZhihao} positionInSet={i + 1} onEdit={(updated) => model.updateStamp(i, updated)}>
              {#snippet leading()}
                {#if !model.isReadonly}
                  <!-- The stamp's Zhihao position is its place in this list, so reordering is
                       the only way to correct a set captured out of order. -->
                  <div class="flex items-center gap-1 shrink-0 text-muted-foreground">
                    <div class="flex flex-col">
                      <button
                        type="button"
                        class="hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
                        disabled={i === 0}
                        title={$t('editor.moveStampUp')}
                        aria-label={$t('editor.moveStampUp')}
                        onclick={() => { pendingRemoval = null; model.moveStamp(i, -1); }}
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        class="hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
                        disabled={i === model.stamps.length - 1}
                        title={$t('editor.moveStampDown')}
                        aria-label={$t('editor.moveStampDown')}
                        onclick={() => { pendingRemoval = null; model.moveStamp(i, 1); }}
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                    <!-- Two steps rather than one: a stamp carries a picture and a dozen
                         fields, and a mis-click would silently throw all of it away. -->
                    {#if pendingRemoval === i}
                      <button
                        type="button"
                        class="text-destructive"
                        title={$t('editor.removeStampConfirm')}
                        aria-label={$t('editor.removeStampConfirm')}
                        onclick={() => { model.removeStamp(i); pendingRemoval = null; }}
                      >
                        <Check size={14} />
                      </button>
                    {:else}
                      <button
                        type="button"
                        class="hover:text-destructive"
                        title={$t('editor.removeStamp')}
                        aria-label={$t('editor.removeStamp')}
                        onclick={() => pendingRemoval = i}
                      >
                        <Trash2 size={14} />
                      </button>
                    {/if}
                  </div>
                {/if}
              {/snippet}
            </StampInSetRow>
          </div>
          {/each}
        </div>
        <br>
  {#if !model.isReadonly}
        <Button onclick={() => {
          const lastStamp = model.stamps.length > 0 ? model.stamps[model.stamps.length - 1] : null;
          stampInSetModel = new StampInSetModel({
            onCreate: model.onStampAdded.bind(model),
            isReadonly: model.isReadonly,
            initialValue: lastStamp,
            defaultTitle: model.title,
            parentZhihao: model.zhihaoForStampCount(model.stamps.length + 1),
            positionInSet: model.stamps.length + 1,
          });
          isSetInStampEditorOpen = true;
        }}>{$t('editor.add')}</Button>
  {/if}
      </div>
      <div class="flex gap-2">
        <ZhihaoEditor isReadonly={model.isReadonly} bind:zhihao={model.zhihao} dateOfIssue={model.date_of_issue} suggestedSetSize={model.suggestedSetSize} setNotation="derivedPosition" containerClass="contents" />
        <LabelsEditor isReadonly={model.isReadonly} bind:labels={model.categories} availableLabels={getLabelsForType(PhilatelicItemType.Set)} containerClass="contents" />
      </div>
    </div>

  {#if model.showError}
    <div class="p-4 text-sm text-red-500 bg-red-50 rounded-md">
      {model.error ? $t(model.error) : ''}
    </div>
  {/if}

  {#if model.isReview}
    <DialogFooter>
      {#if model.isDirty}
        <form onsubmit={(ev: Event) => { model.onsubmit(()=>{open.current = false;}, ev) }}>
          <Button type="submit" disabled={!model.isValid}>{$t('editor.save')}</Button>
        </form>
      {/if}
      <Button onclick={() => model.onreview('Accepted', () => { open.current = false; })}>{$t('editor.accept')}</Button>
      <Button onclick={() => model.onreview('Rejected', () => { open.current = false; })}>{$t('editor.reject')}</Button>
    </DialogFooter>
  {:else if model.isDelete}
    <div class="p-4 text-lg font-bold text-red-600 bg-red-50 rounded-md text-center">
      {$t('deleteSuggestion.warning')}
    </div>
    <DialogFooter>
      <Button variant="outline" onclick={() => open.current = false}>{$t('deleteSuggestion.cancel')}</Button>
      <Button variant="destructive" onclick={() => model.ondelete(() => { open.current = false; })}>{$t('deleteSuggestion.confirm')}</Button>
    </DialogFooter>
  {:else if model.showSubmitButton}
    <form onsubmit={(ev: Event) => { model.onsubmit(()=>{open.current = false;}, ev) }}>
      <DialogFooter>
        <Button type="submit" disabled={!model.isDirty || !model.isValid}>{$t(model.isEdit ? 'editor.save' : 'editor.addSetSuggestion')}</Button>
      </DialogFooter>
    </form>
  {/if}
  </DialogContent>
</Dialog>
<StampInSetEditor
  model={stampInSetModel}
  bind:isOpen={isSetInStampEditorOpen}
/>
